import React, { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Stack,
  Alert,
} from "@mui/material";

const TransferForm = () => {
  const [formData, setFormData] = useState({
    pharmacy_name: "",
    pharmacy_number: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    date_of_birth: "",
    postal_code: "",
    email: "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  // --- validation helpers ---
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const onlyDigits = /^[0-9]+$/;
  const canadaPostal = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
  const usPostal = /^\d{5}(?:-\d{4})?$/;

  const computeErrors = ({
    email,
    phone_number,
    pharmacy_number,
    postal_code,
    date_of_birth,
  }) => {
    // 1) DOB must not be in the future
    if (date_of_birth) {
      const dob = new Date(date_of_birth);
      const today = new Date();
      dob.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      if (dob > today) {
        setError("Date of birth can’t be in the future.");
        return true;
      }
    }

    // 2) Email
    if (email && !emailRegex.test(email)) {
      setError("Invalid email address");
      return true;
    }

    // 3) Patient phone
    if (phone_number && !onlyDigits.test(phone_number)) {
      setError("Patient phone: only digits allowed");
      return true;
    }
    if (
      phone_number &&
      (phone_number.length < 10 || phone_number.length > 11)
    ) {
      setError("Patient phone: must be 10–11 digits");
      return true;
    }

    // 4) Pharmacy phone
    if (pharmacy_number && !onlyDigits.test(pharmacy_number)) {
      setError("Pharmacy number: only digits allowed");
      return true;
    }
    if (
      pharmacy_number &&
      (pharmacy_number.length < 10 || pharmacy_number.length > 11)
    ) {
      setError("Pharmacy number: must be 10–11 digits");
      return true;
    }

    // 5) Postal code (CA or US)
    if (
      postal_code &&
      !canadaPostal.test(postal_code) &&
      !usPostal.test(postal_code)
    ) {
      setError(
        "Invalid postal code. Enter a Canadian code (A1A 1A1) or U.S. ZIP (12345 or 12345-6789)."
      );
      return true;
    }

    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // run all validations
    if (computeErrors(formData)) return;

    // add transfer_date
    const currentDate = new Date().toISOString().split("T")[0];
    const payload = { ...formData, transfer_date: currentDate };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/transfers/create-transfer`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const { error: msg } = await res.json();
        throw new Error(msg || "Failed to submit transfer request.");
      }
      setSuccess(
        "Transfer request submitted successfully! Our pharmacy will call you soon."
      );
      setFormData({
        pharmacy_name: "",
        pharmacy_number: "",
        first_name: "",
        last_name: "",
        phone_number: "",
        date_of_birth: "",
        postal_code: "",
        email: "",
      });
    } catch (err) {
      console.error(err);
      setError(err.message || "An unexpected error occurred.");
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ maxWidth: 500, mx: "auto", p: 2 }}
    >
      <Typography variant="h4" align="center" mb={2}>
        Transfer Request
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Stack spacing={2}>
        <TextField
          label="Pharmacy Name"
          name="pharmacy_name"
          fullWidth
          required
          value={formData.pharmacy_name}
          onChange={handleChange}
        />
        <TextField
          label="Pharmacy Number"
          name="pharmacy_number"
          type="tel"
          fullWidth
          required
          value={formData.pharmacy_number}
          onChange={handleChange}
        />
        <TextField
          label="Patient First Name"
          name="first_name"
          fullWidth
          required
          value={formData.first_name}
          onChange={handleChange}
        />
        <TextField
          label="Patient Last Name"
          name="last_name"
          fullWidth
          required
          value={formData.last_name}
          onChange={handleChange}
        />
        <TextField
          label="Phone Number"
          name="phone_number"
          type="tel"
          fullWidth
          required
          value={formData.phone_number}
          onChange={handleChange}
        />
        <TextField
          label="Email Address"
          name="email"
          type="email"
          fullWidth
          required
          value={formData.email}
          onChange={handleChange}
        />
        <TextField
          label="Date of Birth"
          name="date_of_birth"
          type="date"
          fullWidth
          required
          value={formData.date_of_birth}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Postal Code"
          name="postal_code"
          fullWidth
          required
          value={formData.postal_code}
          onChange={handleChange}
        />

        <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>
          Submit Transfer
        </Button>
      </Stack>
    </Box>
  );
};

export default TransferForm;
